import React, { HTMLAttributes, forwardRef, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

type Variant = 'default' | 'elevated' | 'outlined';
type Padding = 'none' | 'sm' | 'md' | 'lg';

interface BaseCardProps {
  variant?: Variant;
  padding?: Padding;
  /**
   * Deprecated: kept for backwards-compat. If onClick or href is set, the card
   * is automatically rendered as an interactive element. `clickable` alone
   * (without onClick/href) still adds the clickable visual styling to a plain
   * div — prefer wiring up onClick/href instead.
   */
  clickable?: boolean;
  onClick?: () => void;
  href?: string;
  className?: string;
  children?: React.ReactNode;
}

type CardProps = BaseCardProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'onClick'>;

const variantStyles: Record<Variant, string> = {
  default: 'bg-white border border-charcoal-100',
  elevated: 'bg-white shadow-lg',
  outlined: 'bg-white border-2 border-charcoal-200',
};

const paddingStyles: Record<Padding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const baseClasses = 'rounded-2xl transition-all duration-300 overflow-hidden';
const clickableClasses =
  'cursor-pointer hover:border-cherry-200 hover:bg-cherry-50/30 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-cherry-600 focus-visible:ring-offset-2';

/**
 * Card is a container component. When given `onClick` it renders as a
 * `<button>` (with Enter/Space keyboard activation) and when given `href`
 * it renders as a react-router `<Link>`. Otherwise it is a plain `<div>`.
 */
export const Card = forwardRef<HTMLElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      clickable = false,
      onClick,
      href,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isInteractive = Boolean(onClick) || Boolean(href);
    const showClickableStyles = isInteractive || clickable;

    const combinedClassName = [
      baseClasses,
      variantStyles[variant],
      paddingStyles[padding],
      showClickableStyles ? clickableClasses : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={href}
          onClick={onClick}
          className={combinedClassName}
          {...(props as unknown as Omit<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            'href' | 'onClick'
          >)}
        >
          {children}
        </Link>
      );
    }

    if (onClick) {
      const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      };

      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          onClick={onClick}
          onKeyDown={handleKeyDown}
          className={`${combinedClassName} text-left w-full`}
          {...(props as unknown as Omit<
            React.ButtonHTMLAttributes<HTMLButtonElement>,
            'onClick' | 'type'
          >)}
        >
          {children}
        </button>
      );
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={combinedClassName}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}

export function CardHeader({
  title,
  subtitle,
  action,
  className = '',
  ...props
}: CardHeaderProps): React.ReactElement {
  return (
    <div
      className={`flex items-start justify-between mb-4 ${className}`}
      {...props}
    >
      <div>
        <h3 className="font-semibold text-charcoal-900">{title}</h3>
        {subtitle && (
          <p className="text-sm text-charcoal-500 mt-0.5">{subtitle}</p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}
